import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  faArrowTrendUp,
  faBellConcierge,
  faBoxOpen,
  faChartColumn,
  faPercent,
  faPuzzlePiece,
  faRankingStar,
  faReceipt,
  faTicket,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { convertToLocalMoney, fromInputDate } from "../../Objects/Generics/Helps"
import { companyMemory, orderMemory } from "../../Objects/Memory/Memory"
import { useGetOrder } from "../../hooks/Order/useGetOrder"
import { getStoreNow } from "../../utils/timeZone"
import { CashFlowPeriodFilterPopup } from "./CashFlowPeriodFilterPopup"
import { CashFlowMetricsLoading } from "./CashFlowMetricsLoading"
import {
  buildCompactLabelValueListHtml,
  buildCompactSectionHtml,
  buildSummaryCardsHtml,
  buildTableSectionHtml,
  escapeHtml,
  openPrintReport,
} from "../../utils/printReport"
import {
  buildCashFlowMetricsCacheKey,
  deleteCashFlowMetricsCache,
  getCashFlowMetricsCache,
  setCashFlowMetricsCache,
} from "./CashFlowMetricsCache"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { loadCashFlowOrders } from "./loadCashFlowOrders"
import { FinancialPageLayout } from "./components/FinancialPageLayout"
import { FinancialPageHeader } from "./components/FinancialPageHeader"
import { FinancialKpiCard as MetricCard } from "./components/FinancialKpiCard"
import { FinancialChartCard } from "./components/FinancialChartCard"
import { FinancialHorizontalBarChart } from "./components/charts/FinancialHorizontalBarChart"
import { FinancialDataTable } from "./components/FinancialDataTable"
import type { FinancialTableColumn } from "./types/financialUi"
import { limitFinancialRanking } from "./utils/financialChartUtils"
import { CashFlowPrintFormatPopup, type CashFlowPrintFormat } from "./CashFlowPrintFormatPopup"

type MetricRow = { name: string; quantity: number; revenue: number; orders: number; averageTicket: number }

interface MenuMetricsCacheValue {
  orders: OrderEntity[]
}

const toPercent = (value: number) => `${value.toFixed(1)}%`

const getRankBadge = (index: number, t: (key: string) => string) => {
  if (index === 0) return { label: t("menu_metrics.top_1"), color: "bg-amber-100 text-amber-800 border-amber-300" }
  if (index === 1) return { label: t("menu_metrics.top_2"), color: "bg-slate-100 text-slate-800 border-slate-300" }
  if (index === 2) return { label: t("menu_metrics.top_3"), color: "bg-orange-100 text-orange-800 border-orange-300" }
  return { label: `#${index + 1}`, color: "bg-zinc-100 text-zinc-800 border-zinc-300" }
}

const addDaysToInputDate = (inputDate: string, days: number) => {
  const date = new Date(`${inputDate}T00:00:00`)
  date.setDate(date.getDate() + days)
  return date.toInputDate()
}

const getMetricRowKey = (row: MetricRow) => row.name

const getMetricSearchText = (row: MetricRow) => row.name

export const MenuMetricsPage: React.FC = () => {
  const { t } = useTranslation("pages")

  const location = useLocation()

  const [today] = useState(() => getStoreNow(companyMemory.timeZoneId))

  const { handleGetOrder } = useGetOrder()

  const handleGetOrderRef = useRef(handleGetOrder)

  const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toInputDate())

  const [endDate, setEndDate] = useState(today.toInputDate())

  const [reloadFlag, setReloadFlag] = useState(0)

  const [showPeriodFilter, setShowPeriodFilter] = useState(false)

  const [showPrintFormatPopup, setShowPrintFormatPopup] = useState(false)

  const [isMetricsLoading, setIsMetricsLoading] = useState(false)

  const [refreshNonce, setRefreshNonce] = useState(0)

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
  }, [handleGetOrder])

  useEffect(() => {
    let isMounted = true

    const loadMenuMetrics = async () => {
      const cacheKey = buildCashFlowMetricsCacheKey("menu-metrics", startDate, endDate)

      const cachedMetrics = getCashFlowMetricsCache<MenuMetricsCacheValue>(cacheKey)

      if (cachedMetrics) {
        orderMemory.setList(cachedMetrics.orders)
        setReloadFlag((currentValue) => currentValue + 1)
        return
      }

      setIsMetricsLoading(true)

      try {
        const inclusiveEndDate = addDaysToInputDate(endDate, 1)
        await loadCashFlowOrders(handleGetOrderRef.current, startDate, inclusiveEndDate)

        if (!isMounted) {
          return
        }

        setCashFlowMetricsCache<MenuMetricsCacheValue>(cacheKey, { orders: [...orderMemory.listData] })
        setReloadFlag((currentValue) => currentValue + 1)
      } finally {
        if (isMounted) {
          setIsMetricsLoading(false)
        }
      }
    }

    void loadMenuMetrics()

    return () => {
      isMounted = false
    }
  }, [endDate, location.pathname, refreshNonce, startDate])

  const metrics = useMemo(() => {
    void reloadFlag
    const productMap = new Map<string, MetricRow>()

    const additionalMap = new Map<string, MetricRow>()

    const couponMap = new Map<string, MetricRow>()

    const serviceMap = new Map<string, MetricRow>()

    let closedOrders = 0

    let totalRevenue = 0

    let totalOrders = 0

    orderMemory.listData.forEach((order) => {
      if (order.isCancel) {
        return
      }

      totalOrders += 1
      totalRevenue += order.orderPaymentEntity?.totalFinal ?? 0

      if (order.orderPaymentEntity?.isClosed()) {
        closedOrders += 1
      }

      order.getAllCatalogProducts().forEach((item) => {
        const productName = item.getName()
        const itemRevenue = item.valuePaid || item.getValueWithAddtions()
        const currentMetric = productMap.get(productName) ?? {
          name: productName,
          quantity: 0,
          revenue: 0,
          orders: 0,
          averageTicket: 0,
        }

        currentMetric.quantity += 1
        currentMetric.orders += 1
        currentMetric.revenue += itemRevenue
        currentMetric.averageTicket = currentMetric.orders > 0 ? currentMetric.revenue / currentMetric.orders : 0
        productMap.set(productName, currentMetric)

        item.addtionals.forEach((additional) => {
          const additionalName = additional.addtionalEntity?.name ?? t("menu_metrics.additional_fallback")
          const additionalMetric = additionalMap.get(additionalName) ?? {
            name: additionalName,
            quantity: 0,
            revenue: 0,
            orders: 0,
            averageTicket: 0,
          }

          additionalMetric.quantity += additional.amount
          additionalMetric.orders += 1
          additionalMetric.revenue += additional.valueTotal
          additionalMetric.averageTicket =
            additionalMetric.orders > 0 ? additionalMetric.revenue / additionalMetric.orders : 0
          additionalMap.set(additionalName, additionalMetric)
        })
      })

      order.extraServices.forEach((service) => {
        const serviceMetric = serviceMap.get(service.name) ?? {
          name: service.name,
          quantity: 0,
          revenue: 0,
          orders: 0,
          averageTicket: 0,
        }

        serviceMetric.quantity += 1
        serviceMetric.orders += 1
        serviceMetric.revenue += service.valueBase
        serviceMetric.averageTicket = serviceMetric.orders > 0 ? serviceMetric.revenue / serviceMetric.orders : 0
        serviceMap.set(service.name, serviceMetric)
      })

      if (order.couponCode) {
        const couponMetric = couponMap.get(order.couponCode) ?? {
          name: order.couponCode,
          quantity: 0,
          revenue: 0,
          orders: 0,
          averageTicket: 0,
        }

        couponMetric.quantity += 1
        couponMetric.orders += 1
        couponMetric.revenue += order.orderPaymentEntity?.totalCupom ?? 0
        couponMetric.averageTicket = couponMetric.orders > 0 ? couponMetric.revenue / couponMetric.orders : 0
        couponMap.set(order.couponCode, couponMetric)
      }
    })

    const products = [...productMap.values()].sort(
      (firstMetric, secondMetric) => secondMetric.revenue - firstMetric.revenue,
    )
    const additionals = [...additionalMap.values()].sort(
      (firstMetric, secondMetric) => secondMetric.quantity - firstMetric.quantity,
    )
    const coupons = [...couponMap.values()].sort(
      (firstMetric, secondMetric) => secondMetric.quantity - firstMetric.quantity,
    )
    const services = [...serviceMap.values()].sort(
      (firstMetric, secondMetric) => secondMetric.revenue - firstMetric.revenue,
    )

    return { totalOrders, closedOrders, totalRevenue, products, additionals, coupons, services }
  }, [reloadFlag, t])

  const topProductByRevenue = metrics.products[0]
  const topProductByQuantity = [...metrics.products].sort(
    (firstMetric, secondMetric) => secondMetric.quantity - firstMetric.quantity,
  )[0]
  const worstProductByQuantity =
    metrics.products.length > 0
      ? [...metrics.products].sort((firstMetric, secondMetric) => firstMetric.quantity - secondMetric.quantity)[0]
      : undefined
  const averageOrderValue = metrics.closedOrders > 0 ? metrics.totalRevenue / metrics.closedOrders : 0

  const handleApplyPeriodFilter = (filters: { dateStart: string; dateEnd: string }, forceRefresh?: boolean) => {
    if (forceRefresh) {
      deleteCashFlowMetricsCache(buildCashFlowMetricsCacheKey("menu-metrics", filters.dateStart, filters.dateEnd))

      if (filters.dateStart === startDate && filters.dateEnd === endDate) {
        setRefreshNonce((currentValue) => currentValue + 1)
      }
    }

    setStartDate(filters.dateStart)
    setEndDate(filters.dateEnd)
    setShowPeriodFilter(false)
  }

  const handlePrintReport = (format: CashFlowPrintFormat) => {
    const summaryCards = [
      {
        label: t("menu_metrics.orders_in_period"),
        value: metrics.totalOrders.toString(),
        subtitle: `${t("menu_metrics.closed_label")}: ${metrics.closedOrders}`,
      },
      { label: t("menu_metrics.menu_revenue"), value: convertToLocalMoney(metrics.totalRevenue) },
      { label: t("menu_metrics.avg_ticket"), value: convertToLocalMoney(averageOrderValue) },
      {
        label: t("menu_metrics.best_seller"),
        value: topProductByQuantity?.name ?? t("menu_metrics.no_data"),
        subtitle: `${t("menu_metrics.qty_label")}: ${topProductByQuantity?.quantity ?? 0}`,
      },
      {
        label: t("menu_metrics.top_revenue_product"),
        value: topProductByRevenue?.name ?? t("menu_metrics.no_data"),
        subtitle: convertToLocalMoney(topProductByRevenue?.revenue ?? 0),
      },
      {
        label: t("menu_metrics.lowest_output_product"),
        value: worstProductByQuantity?.name ?? t("menu_metrics.no_data"),
        subtitle: `${t("menu_metrics.qty_label")}: ${worstProductByQuantity?.quantity ?? 0}`,
      },
    ]

    const productsTableHtml = buildTableSectionHtml<MetricRow>(
      t("menu_metrics.products_ranking"),
      [
        { header: t("menu_metrics.product"), render: (row) => escapeHtml(row.name) },
        { header: t("menu_metrics.quantity"), render: (row) => row.quantity.toString() },
        { header: t("menu_metrics.orders"), render: (row) => row.orders.toString() },
        { header: t("menu_metrics.revenue"), render: (row) => escapeHtml(convertToLocalMoney(row.revenue)) },
        { header: t("menu_metrics.ticket"), render: (row) => escapeHtml(convertToLocalMoney(row.averageTicket)) },
        {
          header: t("menu_metrics.share"),
          render: (row) =>
            escapeHtml(toPercent(metrics.totalRevenue > 0 ? (row.revenue / metrics.totalRevenue) * 100 : 0)),
        },
      ],
      metrics.products.slice(0, 30),
      t("menu_metrics.no_closed_orders"),
    )

    const buildRowsTableHtml = (title: string, rows: MetricRow[]) =>
      buildTableSectionHtml<MetricRow>(
        title,
        [
          { header: t("menu_metrics.name"), render: (row) => escapeHtml(row.name) },
          { header: t("menu_metrics.quantity"), render: (row) => row.quantity.toString() },
          { header: t("menu_metrics.revenue"), render: (row) => escapeHtml(convertToLocalMoney(row.revenue)) },
        ],
        rows.slice(0, 20),
        t("menu_metrics.not_enough_data"),
      )

    const toCompactMetricRows = (rows: MetricRow[]) =>
      rows.slice(0, 8).map((row) => ({
        label: row.name,
        value: convertToLocalMoney(row.revenue),
        subtitle: `${t("menu_metrics.quantity")}: ${row.quantity} • ${t("menu_metrics.orders")}: ${row.orders}`,
      }))

    const bodyHtml =
      format === "thermal"
        ? [
            buildCompactLabelValueListHtml(summaryCards),
            buildCompactSectionHtml(t("menu_metrics.products_ranking"), toCompactMetricRows(metrics.products)),
            buildCompactSectionHtml(t("menu_metrics.most_used_additionals"), toCompactMetricRows(metrics.additionals)),
            buildCompactSectionHtml(t("menu_metrics.most_used_coupons"), toCompactMetricRows(metrics.coupons)),
            buildCompactSectionHtml(t("menu_metrics.extra_services"), toCompactMetricRows(metrics.services)),
          ].join("")
        : [
            buildSummaryCardsHtml(summaryCards),
            productsTableHtml,
            buildRowsTableHtml(t("menu_metrics.most_used_additionals"), metrics.additionals),
            buildRowsTableHtml(t("menu_metrics.most_used_coupons"), metrics.coupons),
            buildRowsTableHtml(t("menu_metrics.extra_services"), metrics.services),
          ].join("")

    openPrintReport({
      title: t("label_menu_metrics"),
      subtitle: [
        companyMemory.companyName || t("cashflow.print_report.store_fallback"),
        t("cashflow.print_report.period_label", {
          startDate: fromInputDate(startDate).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(endDate).toLocaleDateString("pt-BR"),
        }),
        t("cashflow.print_report.generated_at_label", {
          dateTime: getStoreNow(companyMemory.timeZoneId).toLocaleDateTimeOrDash(
            "pt-BR",
            companyMemory.timeZoneId,
          ),
        }),
      ].join(" • "),
      bodyHtml,
      format,
    })
  }

  const handleSelectPrintFormat = (format: CashFlowPrintFormat) => {
    setShowPrintFormatPopup(false)
    handlePrintReport(format)
  }

  const productRankByName = useMemo(
    () => new Map(metrics.products.map((product, index) => [product.name, index])),
    [metrics.products],
  )

  const productColumns = useMemo<FinancialTableColumn<MetricRow>[]>(
    () => [
      {
        id: "ranking",
        label: t("menu_metrics.ranking"),
        sortValue: (row) => productRankByName.get(row.name) ?? Number.MAX_SAFE_INTEGER,
        render: (row) => {
          const badge = getRankBadge(productRankByName.get(row.name) ?? 0, (key) => t(key))
          return <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-bold ${badge.color}`}>{badge.label}</span>
        },
      },
      {
        id: "product",
        label: t("menu_metrics.product"),
        sortValue: (row) => row.name,
        mobilePriority: "primary",
        render: (row) => <span className="font-extrabold text-slate-900">{row.name}</span>,
      },
      {
        id: "quantity",
        label: t("menu_metrics.quantity"),
        align: "right",
        sortValue: (row) => row.quantity,
        mobilePriority: "summary",
        render: (row) => row.quantity,
      },
      {
        id: "orders",
        label: t("menu_metrics.orders"),
        align: "right",
        sortValue: (row) => row.orders,
        render: (row) => row.orders,
      },
      {
        id: "revenue",
        label: t("menu_metrics.revenue"),
        align: "right",
        sortValue: (row) => row.revenue,
        mobilePriority: "summary",
        render: (row) => <span className="font-extrabold text-emerald-700">{convertToLocalMoney(row.revenue)}</span>,
      },
      {
        id: "ticket",
        label: t("menu_metrics.ticket"),
        align: "right",
        sortValue: (row) => row.averageTicket,
        render: (row) => convertToLocalMoney(row.averageTicket),
      },
      {
        id: "share",
        label: t("menu_metrics.share"),
        align: "right",
        sortValue: (row) => row.revenue,
        render: (row) => toPercent(metrics.totalRevenue > 0 ? (row.revenue / metrics.totalRevenue) * 100 : 0),
      },
    ],
    [metrics.totalRevenue, productRankByName, t],
  )

  const compactColumns = useMemo<FinancialTableColumn<MetricRow>[]>(
    () => [
      {
        id: "name",
        label: t("menu_metrics.name"),
        sortValue: (row) => row.name,
        mobilePriority: "primary",
        render: (row) => <span className="font-extrabold text-slate-900">{row.name}</span>,
      },
      {
        id: "quantity",
        label: t("menu_metrics.quantity"),
        align: "right",
        sortValue: (row) => row.quantity,
        mobilePriority: "summary",
        render: (row) => row.quantity,
      },
      {
        id: "revenue",
        label: t("menu_metrics.revenue"),
        align: "right",
        sortValue: (row) => row.revenue,
        mobilePriority: "summary",
        render: (row) => <span className="font-extrabold text-emerald-700">{convertToLocalMoney(row.revenue)}</span>,
      },
    ],
    [t],
  )

  const paginationLabels = {
    ariaLabel: t("cashflow.table_pagination_aria"),
    previous: t("cashflow.pagination_previous"),
    next: t("cashflow.pagination_next"),
    page: (current: number, total: number) => t("cashflow.pagination_page", { current, total }),
    range: (first: number, last: number, total: number) => t("cashflow.pagination_range", { first, last, total }),
    itemsPerPage: t("cashflow.pagination_size"),
  }

  const productRevenueChart = limitFinancialRanking(
    metrics.products.map((product) => ({ label: product.name, value: product.revenue })),
  )
  const productQuantityChart = limitFinancialRanking(
    metrics.products.map((product) => ({ label: product.name, value: product.quantity })),
  )

  const secondaryTables = [
    { title: t("menu_metrics.most_used_additionals"), rows: metrics.additionals, icon: faPuzzlePiece },
    { title: t("menu_metrics.most_used_coupons"), rows: metrics.coupons, icon: faTicket },
    { title: t("menu_metrics.extra_services"), rows: metrics.services, icon: faBellConcierge },
  ]

  return (
    <FinancialPageLayout>
      {showPeriodFilter && (
        <CashFlowPeriodFilterPopup
          initialFilters={{ dateStart: startDate, dateEnd: endDate }}
          today={today}
          onApplyFilters={handleApplyPeriodFilter}
          onClose={() => setShowPeriodFilter(false)}
        />
      )}

      {showPrintFormatPopup && (
        <CashFlowPrintFormatPopup
          onSelectFormat={handleSelectPrintFormat}
          onClose={() => setShowPrintFormatPopup(false)}
        />
      )}

      <FinancialPageHeader
        title={t("label_menu_metrics")}
        subtitle={t("menu_metrics.page_subtitle")}
        period={t("cashflow.print_report.period_label", {
          startDate: fromInputDate(startDate).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(endDate).toLocaleDateString("pt-BR"),
        })}
        filterLabel={t("cashflow.filter_action")}
        printLabel={t("cashflow.print_action")}
        isLoading={isMetricsLoading}
        onFilter={() => setShowPeriodFilter(true)}
        onPrint={() => setShowPrintFormatPopup(true)}
      />

      <CashFlowMetricsLoading isVisible={isMetricsLoading} />

      {!isMetricsLoading && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <MetricCard
              title={t("menu_metrics.orders_in_period")}
              value={metrics.totalOrders.toString()}
              icon={faReceipt}
              subtitle={`${t("menu_metrics.closed_label")}: ${metrics.closedOrders}`}
              tone="info"
            />
            <MetricCard
              title={t("menu_metrics.menu_revenue")}
              value={convertToLocalMoney(metrics.totalRevenue)}
              icon={faChartColumn}
              subtitle={t("menu_metrics.closed_orders_sum")}
              tone="positive"
            />
            <MetricCard
              title={t("menu_metrics.avg_ticket")}
              value={convertToLocalMoney(averageOrderValue)}
              icon={faPercent}
              subtitle={t("menu_metrics.avg_ticket_formula")}
              tone="violet"
            />
            <MetricCard
              title={t("menu_metrics.best_seller")}
              value={topProductByQuantity?.name ?? t("menu_metrics.no_data")}
              icon={faBoxOpen}
              subtitle={`${t("menu_metrics.qty_label")}: ${topProductByQuantity?.quantity ?? 0}`}
              tone="brand"
            />
            <MetricCard
              title={t("menu_metrics.top_revenue_product")}
              value={topProductByRevenue?.name ?? t("menu_metrics.no_data")}
              icon={faArrowTrendUp}
              subtitle={convertToLocalMoney(topProductByRevenue?.revenue ?? 0)}
              tone="positive"
            />
            <MetricCard
              title={t("menu_metrics.lowest_output_product")}
              value={worstProductByQuantity?.name ?? t("menu_metrics.no_data")}
              icon={faTriangleExclamation}
              subtitle={`${t("menu_metrics.qty_label")}: ${worstProductByQuantity?.quantity ?? 0}`}
              tone="warning"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <FinancialChartCard
              title={t("menu_metrics.revenue_chart_title")}
              subtitle={t("menu_metrics.products_ranking_subtitle")}
              icon={faChartColumn}
            >
              <FinancialHorizontalBarChart
                data={productRevenueChart}
                ariaLabel={t("menu_metrics.revenue_chart_aria")}
                emptyTitle={t("menu_metrics.no_closed_orders")}
                formatValue={convertToLocalMoney}
                valueLabel={t("menu_metrics.revenue")}
              />
            </FinancialChartCard>
            <FinancialChartCard title={t("menu_metrics.quantity_chart_title")} icon={faBoxOpen}>
              <FinancialHorizontalBarChart
                data={productQuantityChart}
                ariaLabel={t("menu_metrics.quantity_chart_aria")}
                emptyTitle={t("menu_metrics.no_closed_orders")}
                formatValue={(value) => value.toLocaleString("pt-BR")}
                valueLabel={t("menu_metrics.quantity")}
              />
            </FinancialChartCard>
          </div>

          <FinancialDataTable
            title={t("menu_metrics.products_ranking")}
            subtitle={t("menu_metrics.products_ranking_subtitle")}
            rows={metrics.products}
            getRowKey={getMetricRowKey}
            columns={productColumns}
            searchText={getMetricSearchText}
            searchLabel={t("menu_metrics.search_products")}
            emptyTitle={t("menu_metrics.no_closed_orders")}
            icon={faRankingStar}
            initialSort={{ columnId: "revenue", direction: "descending" }}
            paginationLabels={paginationLabels}
          />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 min-[1900px]:grid-cols-3">
            {secondaryTables.map((section) => (
              <FinancialDataTable
                key={section.title}
                title={section.title}
                rows={section.rows}
                getRowKey={getMetricRowKey}
                columns={compactColumns}
                searchText={getMetricSearchText}
                searchLabel={t("menu_metrics.search_items")}
                emptyTitle={t("menu_metrics.not_enough_data")}
                icon={section.icon}
                headerLayout="stacked"
                initialSort={{ columnId: "quantity", direction: "descending" }}
                initialPageSize={10}
                pageSizeOptions={[10, 20]}
                paginationLabels={paginationLabels}
              />
            ))}
          </div>
        </>
      )}
    </FinancialPageLayout>
  )
}
